<template>
    <div v-if="featureInfo" class="v-atlas-raumplanung-feature-info-overview">



        <h3>
            {{ featureInfo.attributeMetadata.label }}, {{ featureInfo.responseMetadata.timeFrameLabel }}
        </h3>

        <div class="v-atlas-raumplanung-feature-info__mainValue">

            <div>
                {{ formatNumber(featureInfo.currentValue, {
                    numDecimals: featureInfo.attributeMetadata.numDecimals ?? 2
                }) }} {{ featureInfo.attributeMetadata.unit }}
            </div>


        </div>



        <!--#region The main indicator chart -->

        <template v-if="option">
            <h3>Historische Entwicklung</h3>
            <div class="v-atlas-raumplanung-feature-info-overview__chart">
                <VChart ref="chartRef" :option="chartOption as ECBasicOption" autoresize
                    @legendselectchanged="handleLegendSelectChanged" @click="handleChartClick" />
            </div>
            <EChartsLegend :option="option" :selected="selected" @toggle="handleLegendClick" />
        </template>

        <!--#region Compare values table (for timespan attributes, where no chart is available) -->
        <template v-else-if="featureInfo.compareValues?.length">
            <h3>Vergleichswerte</h3>

            <!--
            <p>
                Hinweis: Für Zeitraum-basierte Indikatoren ist keine Visualisierung der historischen Entwicklung
                verfügbar.
            </p>
-->
            <table class="v-atlas-raumplanung-feature-info-overview__compare-table">
                <thead>
                    <tr>
                        <th>Raumeinheit</th>
                        <th class="v-atlas-raumplanung-feature-info-overview__compare-table-value">
                            Wert

                            <template v-if="featureInfo.attributeMetadata.unit">
                                ({{ featureInfo.attributeMetadata.unit }})
                            </template>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="row in featureInfo.compareValues" :key="row.label"
                        :class="{ 'x-is-main-entity': row.isMainEntity }">
                        <td>{{ row.label }}</td>
                        <td class="v-atlas-raumplanung-feature-info-overview__compare-table-value">
                            {{ formatNumber(row.value, {
                                numDecimals: featureInfo.attributeMetadata.numDecimals ?? 2
                            }) }}
                        </td>
                    </tr>
                </tbody>
            </table>
        </template>
        <!--#endregion Compare values table -->

        <template v-else>
            Hinweis: Für Zeitraum-basierte Indikatoren ist keine Visualisierung der historischen Entwicklung verfügbar.
        </template>

        <!--#endregion The main indicator chart -->






        <!--#region Raumplanerische Funktionszuweisungen-->

        <div v-if="featureInfo?.entity?._tags.includes('type:Gemeinde')"
            class="v-atlas-raumplanung-feature-info__funktionszuweisungen">
            <h3>Raumplanerische Funktionszuweisungen</h3>
            <h4>Raumkategorie:</h4>
            {{ featureInfo?.entity?.raumkategorie?.[0]?.value }}

            <h4>Zentralörtlichkeit:</h4>
            {{ featureInfo?.entity?.zentraloertlichkeit?.[0]?.value }}
            <h4>Wohnfunktion:</h4>
            {{ featureInfo?.entity?.wohnfunktion?.[0]?.value }}
            <h4>Gewerbefunktion:</h4>
            {{ featureInfo?.entity?.gewerbefunktion?.[0]?.value }}
        </div>

        <!--#endregion Raumplanerische Funktionszuweisungen-->
    </div>



</template>

<script setup lang="ts">

import { RaumplanungFeatureInfoResponse } from "atlas-shared";
import { formatNumber } from "gertrud";
import VChart from 'vue-echarts';
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';

import {
    TitleComponent,
    TooltipComponent,
    LegendComponent,
    GridComponent,
} from 'echarts/components';

import { LineChart } from 'echarts/charts';

import { computed, onMounted, ref, watch } from "vue";
import { ECBasicOption } from "echarts/types/dist/shared";
import { RaumplanungMapModule } from "./RaumplanungMapModule";
import EChartsLegend from "./EChartsLegend.vue";


const props = defineProps<{
    featureInfo: RaumplanungFeatureInfoResponse
    mapModule: RaumplanungMapModule
}>()


use([
    CanvasRenderer,
    TitleComponent,
    TooltipComponent,
    LegendComponent,
    GridComponent,
    LineChart,
]);



const option = computed(() => {
    return props.featureInfo.eChartsOption
})

//#region Chart option & legend

// The echarts legend can have a variable number of entries with variable
// label lengths, which makes it hard to size the canvas reliably. Instead of
// rendering the legend inside the echarts canvas (where it can be clipped or
// overlap the plot), we hide the echarts legend and render it as a normal
// HTML element below the chart. The chart height then only needs to fit the
// plot area, and the legend can never be clipped or overlap.
//
// The HTML legend is clickable: clicking an item toggles the corresponding
// series via echarts' `legendToggleSelect` action, and the selected state is
// kept in sync through the `legendselectchanged` event.


const chartRef = ref<InstanceType<typeof VChart> | null>(null)

// Formats numbers according to German conventions (decimal comma, dot
// thousands separator). Used for the y-axis labels and the tooltip of the
// chart. This must run on the client: the chart option is transported from
// the server as JSON, and JSON.stringify silently drops function properties,
// so formatter functions cannot be part of the server response.
function formatChartNumber(value: number | null | undefined): string {
    if (value === null || value === undefined || isNaN(Number(value))) return "-"
    const numDecimals = props.featureInfo.attributeMetadata.numDecimals ?? 2
    return new Intl.NumberFormat('de-DE', {
        minimumFractionDigits: numDecimals,
        maximumFractionDigits: numDecimals
    }).format(Number(value))
}

const chartOption = computed(() => {
    const opt = option.value
    if (!opt) return undefined

    // Hide the echarts legend; it is rendered as HTML below the chart.
    return {
        ...opt,
        legend: {
            ...(opt.legend ?? {}),
            show: false
        },
        // Format the y-axis tick labels in German number format.
        yAxis: {
            ...(opt.yAxis ?? {}),
            axisLabel: {
                ...(opt.yAxis?.axisLabel ?? {}),
                formatter: (value: number) => formatChartNumber(value)
            }
        },
        // Show a tooltip with the value of the hovered data point,
        // including the year and the name of the spatial unit.
        tooltip: {
            ...(opt.tooltip ?? {}),
            trigger: "item",
            axisPointer: undefined,
            // Force a maximum tooltip width of 200px and let the spatial
            // unit label wrap within that width. echarts sets
            // `white-space: nowrap` on the tooltip content by default,
            // so it must be overridden here to allow wrapping.
            extraCssText: "max-width: 200px; white-space: normal; word-break: break-word;",
            formatter: (params: any) => {
                const value = params?.value
                const year = params?.name ?? ""
                const spatialUnit = params?.seriesName ?? ""
                return `${spatialUnit}, ${year}<br/><b>${formatChartNumber(value)} ${props.featureInfo.attributeMetadata.unit ?? ""}</b>`
            }
        }
    }
})

// Build the HTML legend items from the echarts legend data + series colors.
// `selected` is a reactive map (keyed by the stable series key) that tracks
// whether the series is currently shown in the chart. It is kept in a separate
// `ref` so that toggling it triggers a re-render of the legend.
//
// The selection is keyed by the stable, role-based series key (not the label),
// so it can be preserved across main-entity switches: when a new main entity is
// selected, the panel is replaced and re-rendered, and the persisted selection
// from the map module is applied to the new series with the same keys.
const selected = ref<Record<string, boolean>>({})

/**
 * Maps a stable series key to the series' `name` (label) in the current chart
 * option. Returns undefined if the key is not present in the current option.
 */
function seriesNameForKey(key: string): string | undefined {
    return option.value?.series?.find((s) => s.key === key)?.name
}

/**
 * Applies the persisted selection from the map module to the current chart's
 * series (matched by stable key) and dispatches the corresponding echarts
 * legend actions so the chart reflects the persisted show/hide state.
 *
 * IMPORTANT: echarts keys its legend selection by the series *name*, and the
 * names change whenever the main entity changes (e.g. "Kreis: Rhein-Neckar-
 * Kreis" → "Kreis: Mannheim"). When the option changes, echarts therefore
 * resets its legend to "all shown". We must explicitly hide every series whose
 * stable key is hidden in the persisted state — we cannot rely on comparing
 * against the (stale) local `selected` state, because that still holds the
 * previous entity's values and would wrongly skip the dispatch.
 */
function applyPersistedSelection() {
    const opt = option.value
    if (!opt?.series) return

    const persisted = props.mapModule.chartSeriesSelection

    // Reset the local selection to defaults (all shown) and re-apply the
    // persisted state, so the legend reflects the persisted show/hide state.
    selected.value = {}

    for (const series of opt.series) {
        const key = series.key
        const name = series.name
        if (!key || !name) continue

        const shouldShow = persisted[key] ?? true
        selected.value[key] = shouldShow

        if (!shouldShow) {
            chartRef.value?.dispatchAction({
                type: "legendUnSelect",
                name
            })
        }
    }
}

// When the chart option changes (e.g. a new main entity was selected and the
// panel was re-rendered), re-apply the persisted selection from the module to
// the new series with the same stable keys. `flush: 'post'` ensures the chart
// has already processed the new option before we dispatch the legend actions.
watch(option, () => {
    applyPersistedSelection()
}, { immediate: true, flush: 'post' })

// The `immediate` watch above runs during setup, before the chart is mounted,
// so `chartRef` is still null and the dispatched legend actions are lost. Once
// the chart is mounted, re-apply the persisted selection so the chart reflects
// the persisted show/hide state.
onMounted(() => {
    applyPersistedSelection()
})

function handleLegendClick(key: string) {
    // Toggle the series visibility in the echarts instance.
    // We manage the selected state optimistically here (instead of relying
    // solely on the `legendselectchanged` event) so that a series can be
    // re-selected even if it has no data. echarts does not reliably fire the
    // event for empty series, which previously made it impossible to re-select
    // them after deselecting.
    const isCurrentlySelected = selected.value[key] ?? true
    selected.value[key] = !isCurrentlySelected

    // Persist the selection in the map module so it survives main-entity switches.
    props.mapModule.chartSeriesSelection = { ...props.mapModule.chartSeriesSelection, [key]: !isCurrentlySelected }

    const name = seriesNameForKey(key)
    if (name) {
        chartRef.value?.dispatchAction({
            type: isCurrentlySelected ? "legendUnSelect" : "legendSelect",
            name
        })
    }
}

function handleLegendSelectChanged(params: any) {
    // Sync the HTML legend's selected state with the echarts legend state.
    // The echarts event reports selection keyed by series `name`; map it back
    // to the stable key via the option's series.
    const newSelected = params?.selected ?? {}
    const nextSelection = { ...props.mapModule.chartSeriesSelection }

    for (const series of option.value?.series ?? []) {
        const key = series.key
        const name = series.name
        if (!key || !name) continue
        if (name in newSelected) {
            nextSelection[key] = newSelected[name]
            selected.value[key] = newSelected[name]
        }
    }

    props.mapModule.chartSeriesSelection = nextSelection
}

/**
 * Handles clicks on the chart. Clicking a year on the x-axis sets that year
 * in the request settings, so the feature info (current value, time frame
 * label) is re-fetched for the clicked year.
 *
 * The x-axis is configured with `triggerEvent: true` on the server, so echarts
 * fires a `click` event with `componentType === "xAxis"` when a year label is
 * clicked. The clicked year is available in `params.value`.
 */
function handleChartClick(params: any) {
    if (params?.componentType !== "xAxis") return

    const year = Number(params.value)
    if (isNaN(year)) return

    const current = props.mapModule.requestSettings
    if (current.year === year) return

    props.mapModule.reactiveData.requestSettings = {
        ...current,
        year,
    }
}

//#endregion Chart option & legend

</script>